class GzVariantPicker extends HTMLElement {
  constructor() {
    super();

    this.buyBox = document.querySelector('product-info');
    this.data = JSON.parse(this.getAttribute('gz-data')) || [];
    this.sectionId = this.getAttribute('gz-data-section-id');
    this.productURL = null;
    this.selectedVariant = JSON.parse(this.getAttribute('gz-data-selected-variant')) || null;
    this.selectedProduct = null;
    this.selectedOptions = {};
    this.productOptions = [];
    this.isInStock = true;
    this.radioInputs = [];
    this.allowedOptions = [];
    this.itemProperties = [];

    this.checkFreeUpgrade();
    this.refreshUI();
  }

  refreshUI() {
    this.setSelectedProduct();
    this.setGroupedOptions();
    this.setStock();
    this.setSelectedColor();
    this.updateAddToCart();
    this.updateOutOfStock();
    this.updateProductTitle();
    this.updateProductPrice();
    this.updateBreadcrumb();
    this.updateURL();
  }

  // Get all unique product options - group of products
  setGroupedOptions() {
    if (!this.data) {
      return;
    }

    const uniqueOptions = new Map();

    // Build variant options from all grouped products
    this.data.forEach(product => {
      // Combine default product options/variants
      [...product.options, 'storage', 'carrier'].forEach((option, optionIndex) => {
        let optionSet = uniqueOptions.get(option) || new Set();
        const optionKey = `option${optionIndex + 1}`;

        // Populate set with variant values or direct product properties
        (product.variants || []).forEach(variant => {
          const optionValue = variant[optionKey] || product[option];
          if (optionValue) optionSet.add(optionValue);
        });

        uniqueOptions.set(option, optionSet);
      });
    });

    this.productOptions = Array.from(uniqueOptions).map(([label, values]) => ({
      label: label,
      options: Array.from(values).map(value => ({
        name: value,
        price: '',
      })),
    }));

    // Order product options & add options prices
    this.setAllowedOptions();
    this.updateOptionsOrder();
    this.setOptionsPrice();
    this.setSelectedOptions();

    // Render product options & attach 'change' event listeners
    this.renderProductOptions();
  }

  // Find the product from this.selectedVariant
  setSelectedProduct() {
    this.selectedProduct =
      this.data.find(product => product.variants.some(variant => variant.id === this.selectedVariant.id)) ||
      this.selectedProduct;
  }

  // Set default options order based on predefined theme settings
  setAllowedOptions() {
    this.allowedOptions = this.getAttribute('gz-variant-options-whitelist')
      ? this.getAttribute('gz-variant-options-whitelist')
          .split(',')
          .map(option => option.trim().toLowerCase())
      : ['color', 'storage', 'carrier', 'cosmetic condition'];
  }

  // Update the options order
  updateOptionsOrder() {
    // Filter out options that are not in the allowed options array
    this.productOptions = this.productOptions.filter(option =>
      this.allowedOptions.includes(option.label.trim().toLowerCase()),
    );

    // Sort the allowed productOptions based on the predefined order in orderedOptions
    const orderedOptions = this.allowedOptions.map(option => option.trim().toLowerCase());
    this.productOptions.sort((a, b) => {
      const indexA = orderedOptions.indexOf(a.label.trim().toLowerCase());
      const indexB = orderedOptions.indexOf(b.label.trim().toLowerCase());
      return indexA - indexB;
    });

    // Sort the 'storage' options within their category by extracting numerical values
    this.productOptions.forEach(option => {
      if (option.label.toLowerCase() === 'storage') {
        option.options.sort((a, b) => {
          const sizeA = parseInt(a.name.match(/\d+/), 10);
          const sizeB = parseInt(b.name.match(/\d+/), 10);
          return sizeA - sizeB;
        });
      }
    });
  }

  // Set options prices
  setOptionsPrice() {
    if (!this.selectedProduct?.variants) {
      return;
    }

    const relevantVariants = this.selectedProduct?.variants.filter(
      variant => variant.option1.toLowerCase() === this.selectedVariant?.option1.toLowerCase(),
    );

    // Update product options prices based on relevant variants
    this.productOptions.forEach(productOption =>
      productOption.options.forEach(option => {
        // Find a matching variant that has the same option name as the current option
        const matchingVariant = relevantVariants.find(
          variant =>
            variant.option1.toLowerCase() === option.name.toLowerCase() ||
            variant.option2.toLowerCase() === option.name.toLowerCase(),
        );

        // If a relevant variant is found, update the price of the option
        if (matchingVariant) {
          option.price = matchingVariant.price;
        }
      }),
    );
  }

  // Set selected product options
  setSelectedOptions() {
    this.selectedOptions = {
      Color: this.selectedVariant.option1,
      'Cosmetic Condition': this.selectedVariant.option2,
      storage: this.selectedProduct.storage,
      carrier: this.selectedProduct.carrier,
    };
  }

  // Render all product options
  renderProductOptions() {
    this.innerHTML = this.productOptions
      .map(option => {
        const { options } = option;
        if (!options || options.length === 0) {
          return '';
        }
        if (option.label.toLowerCase() === 'color') {
          return this.swatchTemplate(option.label, options);
        } else {
          return this.pillsTemplate(option.label, options);
        }
      })
      .join('');

    // Add 'change' event listener
    this.radioInputs = Array.from(this.querySelectorAll('input[type="radio"]'));
    this.radioInputs?.forEach(input => {
      input.addEventListener('change', e => this.handleOptionChange(e));
    });
  }

  // handle variant changes
  handleOptionChange(e) {
    let matchingVariant = null;
    const changedName = e?.target?.name;
    const changedValue = e?.target?.value;

    const checkedRadios = this.radioInputs.filter(input => input.checked);
    checkedRadios.forEach(input => {
      this.selectedOptions[input.name] = input.value;
    });

    this.data.forEach(product => {
      if (
        product.storage?.toLowerCase() === this.selectedOptions.storage?.toLowerCase() &&
        product.carrier?.toLowerCase() === this.selectedOptions.carrier?.toLowerCase()
      ) {
        matchingVariant = product?.variants.find(variant =>
          [variant.option1, variant.option2].every(value => Object.values(this.selectedOptions).includes(value)),
        );
      }
    });

    this.selectedVariant = matchingVariant || this.selectedVariant;

    // Refresh UI when option change
    this.refreshUI();

    // renderProductOptions() rebuilds all radio inputs from scratch on every change,
    // which drops keyboard focus back to <body>. Restore focus to the newly-rendered
    // input matching what the user just selected.
    if (changedName && changedValue) {
      const reselectedInput = this.radioInputs.find(input => input.name === changedName && input.value === changedValue);
      reselectedInput?.focus();
    }

    // Refresh product media images (gallery & thumbnails)
    this.updateMedia();

    // No matching variant found for selected options
    if (!matchingVariant) {
      this.isInStock = false;
      this.updateAddToCart();
    }

    // Publish variantChange event - used by application to subscribe to
    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        variant: this.selectedVariant,
        product: this.selectedProduct,
      },
    });
  }

  // Check if current variant is in stock
  setStock() {
    if (this.selectedVariant.free_upgrade && this.selectedVariant.free_upgrade.available) {
      this.isInStock = true;
    } else {
      this.isInStock = this.selectedVariant.available;
    }
  }

  // [START] - FREE PRODUCT UPGRADE
  // https://gazelle.atlassian.net/wiki/spaces/E2/pages/3743056099/SPIKE+GZL-845+-+Shopify+Functions+Automatic+Dynamic+Discounts
  checkFreeUpgrade() {
    const storageOptions = ["32GB", "64GB", "128GB", "256GB", "512GB", "1TB"];

    // Find next storage option
    const getNextStorage = (currentStorage) => {
      const currentIndex = storageOptions.indexOf(currentStorage);
      return currentIndex >= 0 && currentIndex < storageOptions.length - 1 ? storageOptions[currentIndex + 1] : null;
    };

    this.data.forEach((product) => {
      product.variants.forEach((outOfStockVariant) => {
        if (!outOfStockVariant.available) {

          // Scenario 1: Check for "Unlocked Carrier Upgrade"
          const unlockedProduct = this.data.find((p) => 
            p.carrier.toLowerCase() === "unlocked" && 
            p.storage.toLowerCase() === product.storage.toLowerCase());

          if (unlockedProduct) {
            const upgradeVariant = unlockedProduct.variants.find((v) => 
              v.available === true &&
              v.option1.toLowerCase() === outOfStockVariant.option1.toLowerCase() && 
              v.option2.toLowerCase() === outOfStockVariant.option2.toLowerCase());

            if (upgradeVariant) {
              outOfStockVariant.free_upgrade = upgradeVariant;
              if(this.selectedVariant.id === outOfStockVariant.id) {
                this.selectedVariant = outOfStockVariant;
                this.selectedVariant.free_upgrade = upgradeVariant;
              }
              return;
            }
          }

          // Scenario 2: Check for "Next Storage Upgrade"
          const nextStorage = getNextStorage(product.storage);
          if (nextStorage) {
            const nextStorageProduct = this.data.find((p) => 
              p.carrier.toLowerCase() === product.carrier.toLowerCase() && 
              p.storage.toLowerCase() === nextStorage.toLowerCase());

            if (nextStorageProduct) {
              const upgradeVariant = nextStorageProduct.variants.find((v) => 
                v.available && 
                v.option1.toLowerCase() === outOfStockVariant.option1.toLowerCase() && 
                v.option2.toLowerCase() === outOfStockVariant.option2.toLowerCase());

              if (upgradeVariant) {
                outOfStockVariant.free_upgrade = upgradeVariant;
                if(this.selectedVariant.id === outOfStockVariant.id) {
                  this.selectedVariant = outOfStockVariant;
                  this.selectedVariant.free_upgrade = upgradeVariant;
                }
                return;
              }
            }
          }
        }
      });
    });

    this.updateAddToCart();
  }
  // [END] - FREE PRODUCT UPGRADE

  // Set selected color - swatch label
  setSelectedColor() {
    this.radioInputs.forEach(input => {
      if (input.checked && input.name.toLowerCase() === 'color') {
        document.querySelector('[data-selected-swatch-value]').innerHTML = input.value;
      }
    });
  }

  // Update product price
  updateProductPrice() {
    const { msrp } = this.selectedProduct;
    const { price, compare_at_price } = this.selectedVariant;

    const isOutOfStock = !this.isInStock;
    const isOnSale = compare_at_price > price;

    const productPrices = document.querySelectorAll('.gz-main-product__price');
    productPrices.forEach(productPrice => {
      productPrice.innerHTML = `
        ${msrp ? `<span class="gz-main-product__price__msrp"> $${this.formatPrice(msrp)} new </span>` : ''}
        <div class="price price--large ${isOnSale ? `price--on-sale` : ''} ${
        isOutOfStock ? `price--sold-out` : ''
      } price--show-badge">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">
                $${this.formatPrice(price)}
              </span>
            </div>
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">
                $${this.formatPrice(price)}
              </span>
            </div>
            <small class="unit-price caption hidden">
              <span class="visually-hidden">Unit price</span>
              <span class="price-item price-item--last">
                <span></span>
                <span aria-hidden="true">/</span>
                <span class="visually-hidden">&nbsp;per&nbsp;</span>
                <span></span>
              </span>
            </small>
          </div>
          ${isOnSale ? `<span class="badge price__badge-sale color-scheme-4">Sale</span>` : ''}
          ${isOutOfStock ? `<span class="badge price__badge-sold-out">Sold out</span>` : ''}
        </div>
      `;
    });
  }

  // Update product title
  updateProductTitle() {
    const productTitles = document.querySelectorAll('.product__title');
    productTitles.forEach(title => {
      title.innerHTML = `<div class="product__title">
        <h1>${this.selectedProduct.title}</h1>
      </div>`;
    });
  }

  // update variants/options OOS state
  updateOutOfStock() {
    // Disable all radio button/options initially
    this.radioInputs.forEach(input => {
      input.classList.add('disabled');
    });

    // Extract the selected options
    const { storage: selectedStorage, carrier: selectedCarrier } = this.selectedOptions;
    const { option1: selectedOption1, option2: selectedOption2 } = this.selectedVariant;

    // Re-enable for available product variants (color, condition)
    this.selectedProduct.variants.forEach((variant) => {
      const { option1, option2 } = variant;
      const available = variant.free_upgrade?.available 
        ? variant.free_upgrade.available 
        : variant.available;

      if (available) {
        this.radioInputs.forEach((input) => {
          const inputValue = input.value.trim().toLowerCase();
          const normalizedOption1 = option1.trim().toLowerCase();
          const normalizedOption2 = option2.trim().toLowerCase();
          const normalizedSelectedOption1 = selectedOption1.trim().toLowerCase();
          const normalizedSelectedOption2 = selectedOption2.trim().toLowerCase();

          // Compare the normalized values
          if (
            (normalizedOption1 === normalizedSelectedOption1 && inputValue === normalizedOption2) ||
            (normalizedOption2 === normalizedSelectedOption2 && inputValue === normalizedOption1)
          ) {
            input.classList.remove('disabled');
          }
        });
      }
    });

    // Re-enable for 'storage' and 'carrier'
    this.data.forEach((product) => {
      // Re-enable different storages for 'selectedCarrier'
      if (this.normalizeStr(product.carrier) === this.normalizeStr(selectedCarrier)) {
        product.variants.forEach((variant) => {
          const { option1, option2 } = variant;
          const available = variant.free_upgrade?.available 
            ? variant.free_upgrade.available 
            : variant.available;

          if (
            available &&
            this.normalizeStr(option1) === this.normalizeStr(selectedOption1) &&
            this.normalizeStr(option2) === this.normalizeStr(selectedOption2)
          ) {
            this.radioInputs.forEach(input => {
              const inputName = this.normalizeStr(input.getAttribute('name'));
              const inputValue = this.normalizeStr(input.getAttribute('value'));
              if (inputName === 'storage' && inputValue === this.normalizeStr(product.storage)) {
                input.classList.remove('disabled');
              }
            });
          }
        });
      }

      // Re-enable different carriers for 'selectedStorage'
      if (this.normalizeStr(product.storage) === this.normalizeStr(selectedStorage)) {
        product.variants.forEach((variant) => {
          const { option1, option2 } = variant;
          const available = variant.free_upgrade?.available 
            ? variant.free_upgrade.available 
            : variant.available;

          if (
            available &&
            this.normalizeStr(option1) === this.normalizeStr(selectedOption1) &&
            this.normalizeStr(option2) === this.normalizeStr(selectedOption2)
          ) {
            this.radioInputs.forEach(input => {
              const inputName = this.normalizeStr(input.getAttribute('name'));
              const inputValue = this.normalizeStr(input.getAttribute('value'));
              if (inputName === 'carrier' && inputValue === this.normalizeStr(product.carrier)) {
                input.classList.remove('disabled');
              }
            });
          }
        });
      }
    });

    // Announce out-of-stock state to assistive tech — visual strikethrough/dimming alone
    // does not convey unavailability to screen reader users (applies to every option type:
    // color, storage, carrier, cosmetic condition).
    this.updateOutOfStockAccessibility();
  }

  // Expose out-of-stock state programmatically for every disabled option
  updateOutOfStockAccessibility() {
    this.radioInputs.forEach(input => {
      const isDisabled = input.classList.contains('disabled');
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (!label) return;

      let oosFlag = label.querySelector('.gz-swatch-input__oos-flag');

      if (isDisabled) {
        input.setAttribute('aria-disabled', 'true');
        if (!oosFlag) {
          oosFlag = document.createElement('span');
          oosFlag.className = 'visually-hidden gz-swatch-input__oos-flag';
          oosFlag.textContent = ' — Out of stock';
          label.appendChild(oosFlag);
        }
      } else {
        input.removeAttribute('aria-disabled');
        oosFlag?.remove();
      }
    });
  }

  // Update add-to-cart
  updateAddToCart() {
    // update selected variant [name="id"] - ready for ATC
    const variantIds = document.querySelectorAll(".product-variant-id");
    if (variantIds.length) {
      const idToUse = this.selectedVariant.free_upgrade?.available ? this.selectedVariant.free_upgrade.id : this.selectedVariant.id;
      variantIds.forEach((variantId) => {
        variantId.value = idToUse;
        variantId.setAttribute("value", idToUse);
      });
    }

    // Update Add To Cart button
    const addToCarts = document.querySelectorAll('.product-form__submit');

    addToCarts.forEach(atc => {
      atc.disabled = !this.isInStock;
      atc.innerHTML = `
        ${!this.isInStock ? `<span>Sold out</span>` : '<span>Add to cart</span>'}
        <span class="gz-loader"></span>
      `;
    });

    // Update line-item properties for Free Upgrade
    const upgradedDD = document.querySelector('input[name="__gzdd"]');
    const upgradedDDST = document.querySelector('input[name="__gzddst"]');

    if (this.selectedVariant.free_upgrade) {
      const price = this.selectedVariant.price;
      const upgraded = this.selectedVariant.free_upgrade?.price;
      const difference = this.priceWithoutTrailingZero(upgraded - price);

      if (upgradedDD) {
        const random = String(this.selectedVariant.id).split('').reverse().join('').slice(0, 10);
        upgradedDD.value = `${this.selectedVariant.id}:${difference}${random}`;
      }
    
      if (upgradedDDST) {
        upgradedDDST.value = this.selectedVariant.product?.storage;
      }
    } else {
      if (upgradedDD) upgradedDD.value = '';
      if (upgradedDDST) upgradedDDST.value = '';
    }
  }

  // Update breadcrumb
  updateBreadcrumb() {
    const breadcrumbs = document.querySelector('.breadcrumbs');
    if (!breadcrumbs) return;

    const lastElement = breadcrumbs.lastElementChild;

    if (!lastElement) return;

    if (lastElement.tagName.toLowerCase() === 'a') {
      lastElement.outerHTML = `
        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" aria-current="page">${this.selectedProduct.title}</span>
      `;
    } else if (lastElement.tagName.toLowerCase() === 'span') {
      lastElement.innerHTML = `${this.selectedProduct.title}`;
    }
  }

  // Update the URL - when variant change
  updateURL() {
    if (!this.selectedVariant || !this.selectedVariant.url) {
      return;
    }

    const currentURL = new URL(window.location.href);
    const newURL = `${currentURL.origin}${this.selectedVariant.url}`;
    this.productURL = newURL;
    window.history.pushState({ path: newURL }, '', newURL);
  }

  // Update gallery and thumbnails
  updateMedia() {
    let featured_media = this.selectedVariant.featured_media;

    if (featured_media) {
      // current gallery and thumbnails elements
      const mediaGallerySource = document.querySelector(`[id^="MediaGallery-${this.sectionId}"] ul`);
      const mediaThumbsSource = document.querySelector(`[id="Slider-Thumbnails-${this.sectionId}"]`);
      const zoomModal = document.querySelector('product-modal .product-media-modal__content');

      // assemble new image product images for selected variant - override the existing images later
      const mediaGalleryDestination = this.productMediaGalleryTemplate();
      const mediaThumbsGalleryDestination = this.productGalleryThumbnailTemplate();
      const zoomModalContent = this.productZoomGalleryTemplate();

      mediaGallerySource.innerHTML = mediaGalleryDestination;
      mediaThumbsSource.innerHTML = mediaThumbsGalleryDestination;
      zoomModal.innerHTML = zoomModalContent;

      const mediaGallery = document.querySelector('media-gallery');

      if (mediaGallery) {
        mediaGallery.refresh();
        mediaGallery?.setActiveMedia?.(`${this.sectionId}-${featured_media.id}`, false);
      }
    }
  }

  // Assmeble product main media gallery template
  productMediaGalleryTemplate() {
    const htmlString = `
      ${this.selectedProduct.media
        .map(
          media => `
        <li id="Slide-${this.sectionId}-${media.id}"
          class="product__media-item grid__item slider__slide scroll-trigger animate--fade-in"
          data-media-id="${this.sectionId}-${media.id}">

          <div class="product-media-container media-type-image media-fit-contain global-media-settings gradient constrain-height"
            style="--ratio: ${media.aspect_ratio}; --preview-ratio: ${media.aspect_ratio};">

            <modal-opener class="product__modal-opener product__modal-opener--image no-js-hidden" data-modal="#ProductModal-${this.sectionId}">
              <span class="product__media-icon motion-reduce quick-add-hidden product__media-icon--lightbox" aria-hidden="true">
                <svg aria-hidden="true" focusable="false" class="icon icon-plus" width="19" height="19"
                  viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M4.66724 7.93978C4.66655 7.66364 4.88984 7.43922 5.16598 7.43853L10.6996 7.42464C10.9758 7.42395 11.2002 7.64724 11.2009 7.92339C11.2016 8.19953 10.9783 8.42395 10.7021 8.42464L5.16849 8.43852C4.89235 8.43922 4.66793 8.21592 4.66724 7.93978Z"
                    fill="currentColor"></path>
                  <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M7.92576 4.66463C8.2019 4.66394 8.42632 4.88723 8.42702 5.16337L8.4409 10.697C8.44159 10.9732 8.2183 11.1976 7.94215 11.1983C7.66601 11.199 7.44159 10.9757 7.4409 10.6995L7.42702 5.16588C7.42633 4.88974 7.64962 4.66532 7.92576 4.66463Z"
                    fill="currentColor"></path>
                  <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M12.8324 3.03011C10.1255 0.323296 5.73693 0.323296 3.03011 3.03011C0.323296 5.73693 0.323296 10.1256 3.03011 12.8324C5.73693 15.5392 10.1255 15.5392 12.8324 12.8324C15.5392 10.1256 15.5392 5.73693 12.8324 3.03011ZM2.32301 2.32301C5.42035 -0.774336 10.4421 -0.774336 13.5395 2.32301C16.6101 5.39361 16.6366 10.3556 13.619 13.4588L18.2473 18.0871C18.4426 18.2824 18.4426 18.599 18.2473 18.7943C18.0521 18.9895 17.7355 18.9895 17.5402 18.7943L12.8778 14.1318C9.76383 16.6223 5.20839 16.4249 2.32301 13.5395C-0.774335 10.4421 -0.774335 5.42035 2.32301 2.32301Z"
                    fill="currentColor"></path>
                </svg>
              </span>

              <div class="loading__spinner hidden">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66"
                  xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>

              <div class="product__media media media--transparent">
                <img src="${media.preview_image_md}" alt="${media.alt}" fetchpriority="high">
              </div>

              <button class="product__media-toggle quick-add-hidden product__media-zoom-lightbox" type="button"
                aria-haspopup="dialog" data-media-id="${media.id}">
                <span class="visually-hidden">
                  Open media ${media.position} in modal
                </span>
              </button>
            </modal-opener>
          </div>
        </li>
      `,
        )
        .join('')}
    `;

    return htmlString;
  }

  // Assmeble product gallery thumbnails template
  productGalleryThumbnailTemplate() {
    const htmlString = `
      ${this.selectedProduct.media
        .map(
          (media, index) => `
        <li id="Slide-Thumbnails-${this.sectionId}-${index}"
          class="thumbnail-list__item slider__slide animate--fade-in" 
          data-target="${this.sectionId}-${media.id}" 
          data-media-position="${index}">
          <button class="thumbnail global-media-settings global-media-settings--no-shadow" 
            aria-label="Load image ${index} in gallery view"
            aria-controls="GalleryViewer-${this.sectionId}" 
            aria-describedby="Thumbnail-${this.sectionId}-${index}">
            <img src="${media.preview_image_xs}" alt="${media.alt}" id="Thumbnail-${this.sectionId}-${index}" />
          </button>
        </li>
      `,
        )
        .join('')}
    `;
    return htmlString;
  }

  // assemble the zoom modal gallery content
  productZoomGalleryTemplate() {
    const featured_media = this.selectedVariant?.featured_media;
    const mediaItems = this.selectedProduct?.media || [];

    let htmlString = '';

    // Include the featured_media if it exists
    if (featured_media) {
      htmlString += `
          <img class="global-media-settings global-media-settings--no-shadow" 
            src="${featured_media.preview_image.src}" 
            alt="${featured_media.alt}"
            loading="lazy"
            width="${featured_media.preview_image.width}"
            height="${featured_media.preview_image.height}"
            data-media-id="${featured_media.id}" 
        />
      `;
    }

    // Loop through media items, including the featured_media if not already included
    htmlString += mediaItems
      .map(media => {
        if (featured_media && media.id === featured_media.id) {
          return ''; // Skip the featured media if it was already added
        }

        const variant_image =
          this.selectedVariant && this.selectedVariant.featured_media?.id === media.id ? true : false;

        return `
        <img class="global-media-settings global-media-settings--no-shadow ${
          variant_image ? 'product__media-item--variant' : ''
        }" 
          src="${media.preview_image_original}" 
          alt="${media.alt}"
          loading="lazy"
          data-media-id="${media.id}" 
        />
      `;
      })
      .join('');

    return htmlString;
  }

  // Swatch option template
  swatchTemplate(label, options) {
    const relevantProducts = this.selectedProduct.variants.filter(
      variant => variant.option2 === this.selectedVariant.option2,
    );
    options = options.map(optn => {
      const matchedProduct = relevantProducts.find(product => product.option1 === optn.name);
      return { ...optn, price: matchedProduct ? matchedProduct.price : null };
    });
    const inputs = options
      .filter(option => option.name)
      .map(option => {
        const id = this.generateUniqueId();
        const isChecked = this.selectedOptions[label] && this.selectedOptions[label] === option.name;
        const swatch =
          window.GzTheme?.SWATCHES.find(color => color.name === option.name.toLowerCase().replace(/\s+/g, '-')) ||
          'transparent';
        const swatchDisplayPrice = ` $${this.priceWithoutTrailingZero(option.price)}`;

        return `
          <input type="radio" class="gz-swatch-input__input" id="${id}" name="${label}" value="${option.name}" ${
          isChecked ? 'checked' : ''
        }/>
          <label for="${id}" title="${option.name}" class="gz-swatch-input__label">
            <span class="gz-swatch" style="--swatch--background: ${swatch?.color}; --swatch--oos-background: ${
          swatch?.opacity
        };"></span>
            <span class="visually-hidden">${option.name}</span>
             <span class="gz-swatch-text">${option.price ? swatchDisplayPrice : ''}</span>
          </label>
        `;
      })
      .join('');

    return `
      <fieldset class="js product-form__input product-form__input--swatch">
        <legend class="form__label">
          ${label}: 
          <span data-selected-swatch-value="Color">${
            this.selectedOptions[label] ? this.selectedOptions[label] : 'Select Color'
          }</span>
        </legend>
        ${inputs}
      </fieldset>
    `;
  }

  // Pills option template
  pillsTemplate(label, options) {
    const shouldDisplayPrice = !['storage', 'carrier'].includes(label.toLowerCase());

    const inputs = options
      .filter(option => option.name)
      .map(option => {
        const id = this.generateUniqueId();
        const isChecked = this.selectedOptions[label] && this.selectedOptions[label] === option.name;
        const conditionDisplayPrice = ` $${this.priceWithoutTrailingZero(option.price)}`;

        let displayLabel = option.name;

        return `
          <input type="radio" id="${id}" name="${label}" value="${option.name}" ${isChecked ? 'checked' : ''}/>
          <label for="${id}">${displayLabel}<br> 
            ${
              shouldDisplayPrice
                ? `<span style="margin-top: 4px; display: inline-block;">${conditionDisplayPrice}</span>`
                : ''
            }
          </label>
        `;
      })
      .join('');

    return `
      <fieldset name="${label}" class="js product-form__input product-form__input--pill">
        <legend class="form__label">${label}</legend>
        ${shouldDisplayPrice ? `<div class="condition-field-wrapper">${inputs}</div>` : inputs}
      </fieldset>
    `;
  }

  // Normalize string (lowercase and trim)
  normalizeStr(str) {
    return typeof str === 'string' ? str.trim().toLowerCase() : str;
  }

  // Generate unique ID
  generateUniqueId() {
    return '_' + Math.random().toString(36).substr(2, 9);
  }

  // Format price (ex: 3500 to 35.00)
  formatPrice(price) {
    return (price / 100).toFixed(2);
  }

  // Format price (ex: 3500 to 35)
  priceWithoutTrailingZero(price) {
    return Math.ceil(price / 100).toString();
  }
}
customElements.define('gz-variant-picker', GzVariantPicker);
